import { useCallback, useEffect, useState } from "react";
import { api, TASK_CLASS_LABELS } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { History, Undo2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DOMAINS = ["", "load", "rehab", "nutrition", "availability", "fixture", "budget", "profile", "activity"];
const SOURCE_LABEL = {
  manual: "Manual edit", bulk_action: "Bulk action", applied_scenario: "Applied scenario",
  fixture: "Fixture change", rollback: "Rollback", activity_done: "Activity completed",
};

/** Unified per-athlete logs: change edits + completed activities · who did what, when, with warnings. */
export default function ChangeLog({ athleteId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [domain, setDomain] = useState("");
  const [role, setRole] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const canRollback = ["admin", "owner", "coach"].includes(user?.role);

  const load = useCallback(async () => {
    if (!athleteId) return;
    const q = new URLSearchParams({ ...(domain && domain !== "activity" && { domain }), ...(role && { role }), ...(start && { start }), ...(end && { end }) });
    try {
      const [cl, tk] = await Promise.all([
        api.get(`/athletes/${athleteId}/change-log?${q}`).then((r) => r.data.entries || []).catch(() => []),
        api.get(`/tasks?athlete_id=${athleteId}`).then((r) => (r.data || []).filter((t) => t.adherence === "done")).catch(() => []),
      ]);
      const doneRows = tk.map((t) => ({
        entry_id: `done_${t.task_id}`,
        at: t.completed_at || `${t.date}T12:00:00`,
        coach: t.completed_by_name || t.athlete_name || "Athlete",
        role: t.completed_by_role || "athlete",
        domain: "activity",
        field: TASK_CLASS_LABELS[t.task_class] || t.task_class,
        old: null,
        new: t.title,
        source: "activity_done",
        note: t.duration_min ? `${t.duration_min}min${t.intensity ? " · " + t.intensity : ""}` : (t.intensity || ""),
        rolled_back: false,
        _done: true,
        _date: t.date,
      }));
      const merged = [...cl, ...doneRows]
        .filter((r) => !domain || domain === "activity" ? (!domain || r.domain === domain) : r.domain === domain)
        .sort((a, b) => (b.at || "").localeCompare(a.at || ""));
      setRows(merged);
    } catch { setRows([]); }
  }, [athleteId, domain, role, start, end]);
  useEffect(() => { load(); }, [load]);

  const rollback = async (e) => {
    if (e._done) return;
    const plan = !!e.snapshot_id;
    if (!window.confirm(plan
      ? "Restore the exact calendar as it was before this change? Sessions, durations, intensities and day types will all go back."
      : "Roll this change back?")) return;
    try {
      const { data } = await api.post(`/athletes/${athleteId}/change-log/${e.entry_id}/rollback`);
      const parts = [];
      if (data.restored_tasks) parts.push(`${data.restored_tasks} session(s) put back`);
      if (data.removed_tasks) parts.push(`${data.removed_tasks} removed`);
      toast.success(parts.length ? `Calendar restored · ${parts.join(", ")}` : "Change rolled back");
      load();
      window.dispatchEvent(new CustomEvent("trifit-synced", { detail: { athleteId } }));
    } catch (err) { toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not roll back"); }
  };

  const roles = Array.from(new Set(rows.map((r) => r.role))).filter(Boolean);

  return (
    <div className="p-4 sm:p-8" data-testid="change-log">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <History className="w-4 h-4" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold flex-1">Logs</span>
        <select data-testid="cl-domain" value={domain} onChange={(e) => setDomain(e.target.value)} className="inp w-auto text-xs py-1.5">
          {DOMAINS.map((d) => <option key={d} value={d}>{d ? d : "All"}</option>)}
        </select>
        <select data-testid="cl-role" value={role} onChange={(e) => setRole(e.target.value)} className="inp w-auto text-xs py-1.5">
          <option value="">All roles</option>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input data-testid="cl-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="inp w-auto text-xs py-1.5" />
        <input data-testid="cl-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="inp w-auto text-xs py-1.5" />
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="cl-empty">
          Nothing recorded yet. Every edit, bulk action, applied scenario, fixture change and completed activity will appear here.
        </p>
      ) : (
        <div className="scroll-x border border-border">
          <table className="w-full text-sm" data-testid="cl-table">
            <thead>
              <tr className="border-b border-border bg-surface text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left p-2">When</th><th className="text-left p-2">Who</th><th className="text-left p-2">Role</th>
                <th className="text-left p-2">Domain</th><th className="text-left p-2">What</th><th className="text-left p-2">Detail</th>
                <th className="text-left p-2">Source</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.entry_id} className={`border-b border-border last:border-0 ${e.rolled_back ? "opacity-50" : ""}`} data-testid={`cl-row-${e.entry_id}`}>
                  <td className="p-2 tabular-nums whitespace-nowrap text-muted-foreground">{e._done ? fmtDate(e._date) : fmtDateTime(e.at)}</td>
                  <td className="p-2 whitespace-nowrap">{e.coach || "-"}</td>
                  <td className="p-2 text-muted-foreground">{e.role}</td>
                  <td className="p-2">
                    {e._done
                      ? <span className="inline-flex items-center gap-1 text-traffic-green"><CheckCircle2 className="w-3.5 h-3.5" /> activity</span>
                      : e.domain}
                  </td>
                  <td className="p-2">{e.field}</td>
                  <td className="p-2 whitespace-nowrap">
                    {e._done ? (
                      <span><b>{e.new}</b>{e.note && <span className="block text-[11px] text-muted-foreground">{e.note}</span>}</span>
                    ) : (
                      <>
                        <span className="text-muted-foreground">{String(e.old ?? "-")}</span> → <b>{String(e.new ?? "-")}</b>
                        {(e.warnings || []).length > 0 && (
                          <span className="block text-[11px] text-traffic-amber mt-0.5" data-testid={`cl-warning-${e.entry_id}`}>
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            {e.warnings.map((w) => w.text).join(" · ")}
                          </span>
                        )}
                        {e.note && <span className="block text-[11px] text-muted-foreground">{e.note}</span>}
                      </>
                    )}
                  </td>
                  <td className="p-2 text-muted-foreground whitespace-nowrap">
                    {SOURCE_LABEL[e.source] || e.source}{e.scenario ? ` · ${e.scenario}` : ""}
                  </td>
                  <td className="p-2 text-right">
                    {!e._done && canRollback && !e.rolled_back && (
                      <button data-testid={`cl-rollback-${e.entry_id}`} onClick={() => rollback(e)}
                        title={e.snapshot_id ? "Restore the exact calendar from before this change" : "Roll back"}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                        <Undo2 className="w-3.5 h-3.5" />
                        {e.snapshot_id && <span className="text-[10px] uppercase tracking-wider" data-testid={`cl-exact-${e.entry_id}`}>exact</span>}
                      </button>
                    )}
                    {e.rolled_back && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">rolled back</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
